import React, {
    useState,
    useEffect,
    useMemo,
    useRef,
    useCallback,
    useLayoutEffect,
    forwardRef,
} from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
    Avatar,
    Badge,
    Dropdown,
    Progress,
    Spin,
    Typography,
    Popover,
    List,
    Button,
    Empty,
    Divider,
} from "antd";
import {
    DownOutlined,
    LoginOutlined,
    CheckOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { authActions } from "../actions/authActions";
import { commonActions } from "../actions/commonAction";
import toast from "../utils/toast";
import { titleCase } from "@/utils/portfolioHelpers";
import { Bell } from "./Icons";
import { Spinner } from "./Spinner";
import { PanelLeft, PanelRight } from "lucide-react";
interface HeaderBarProps {
    onToggle?: () => void;
    collapsed?: boolean;
}

const { Text } = Typography;
const ACTIVE_UPLOAD_NAVIGATION_MESSAGE =
    "Upload is in progress. Please wait for it to finish before leaving this step.";

const HeaderBar = forwardRef<HTMLDivElement, HeaderBarProps>(
    ({ onToggle, collapsed }, headerRef) => {
        const auth = useSelector((state: any) => state.authReducer?.authData);
        const notificationCount: number = useSelector(
            (state: any) => state.getNotificationCount?.count ?? 0,
        );
        const notifySuccess: number = useSelector(
            (state: any) => state.getNotificationCount?.isSuccess ?? false,
        );
        const notificationList: any = useSelector((state: any) => state.listNotificationsReducer);
        const { fileList, loading, pendingUploads, uploadProgress } = useSelector(
            (state: any) => ({
                fileList: state.mediaUpload?.fileList || [],
                loading: Boolean(state.mediaUpload?.loading),
                pendingUploads: Number(state.mediaUpload?.pendingUploads || 0),
                uploadProgress: state.mediaUpload?.uploadProgress || {},
            }),
            shallowEqual,
        );
        const albumUploadByTemplate = useSelector(
            (state: any) => state.albumUpload?.byTemplate ?? {},
            shallowEqual,
        );
        const dispatch = useDispatch();
        const navigate = useNavigate();
        const location = useLocation();
        const [isSmall, setIsSmall] = useState<boolean>(false);
        const [notifOpen, setNotifOpen] = useState(false);
        const [notifPage, setNotifPage] = useState(1);
        const notifScrollRef = useRef<HTMLDivElement>(null);
        const notificationScrollState = useRef<{ scrollTop: number; scrollHeight: number } | null>(
            null,
        );
        const loadingNotifPageRef = useRef<number | null>(null);
        const audioRef = useRef<HTMLAudioElement | null>(null);
        const prevNotificationCountRef = useRef<number>(notificationCount);

        useEffect(() => {
            if (typeof Audio !== "undefined") {
                audioRef.current = new Audio("../assets/audio/notification.mp3");
                audioRef.current.preload = "auto";
            }
        }, []);

        useEffect(() => {
            if (notifySuccess && notificationCount > prevNotificationCountRef.current) {
                try {
                    audioRef.current!.currentTime = 0;
                    audioRef.current!.play().catch(() => {});
                } catch (e) {}
            }
            prevNotificationCountRef.current = notificationCount;
        }, [notificationCount, notifySuccess]);

        const uploadSummary = useMemo(() => {
            const progressValues = Object.values(uploadProgress || {})
                .filter((value): value is number => typeof value === "number")
                .map((value) => Math.max(0, Math.min(100, value)));

            const albumProgressValues = Object.values(albumUploadByTemplate)
                .flatMap((t: any) => Object.values(t?.uploadProgress ?? {}))
                .filter((v): v is number => typeof v === "number")
                .map((v) => Math.max(0, Math.min(100, v)));

            const albumUploading = Object.values(albumUploadByTemplate).some(
                (t: any) => t?.uploading === true,
            );

            const allProgressValues = [...progressValues, ...albumProgressValues];
            const averageProgress = allProgressValues.length
                ? Math.round(
                      allProgressValues.reduce((sum, value) => sum + value, 0) /
                          allProgressValues.length,
                  )
                : 0;

            return {
                isUploading: loading || pendingUploads > 0 || albumUploading,
                totalFiles: fileList.length || allProgressValues.length,
                averageProgress,
            };
        }, [fileList, loading, pendingUploads, uploadProgress, albumUploadByTemplate]);
        const uploadLabel = uploadSummary.totalFiles > 0 ? "Uploading" : "Uploading files";

        // Page title mapping based on route
        const pageTitles: Record<string, string> = {
            "/dashboard": "Dashboard",
            "/users": "Users",
            "/invite": "Invite Users",
            "/referrals": "Referrals",
            "/admin-review": "Onboarding Applications",
            "/referral-requests": "Referral Requests",
            "/studios": "Studios",
            "/settings": "Settings",
            "/profile": "Profile",
            "/account/notifications": "Notification Settings",
            "/change-password": "Change Password",
            "/review": "Review",
            "/event": "Events",
            "/events/management": "Event Management",
            "/users/edit": "Edit user",
            "/users/view": "View user",
            "/studios/view": "View Studio",
            "/studios/edit": "Edit Studio",
            "/subscription-plans/create": "Create Subscription Plan",
            "/subscription-plans/edit": "Edit Subscription Plan",
            "/subscription-plans": "Subscription Plans",
            "/subscription-plans/view": "View Subscription Plan",
            "/subscription-plans/assign": "Assign Subscription Plan",
            "/enquiries": "Enquiries",
            "/album-templates": "Album templates",
            "/routes": "Routes",
        };

        // Get current page title
        const getPageTitle = () => {
            const path = location.pathname;
            // Check for exact match first
            if (pageTitles[path]) return pageTitles[path];
            // Check for partial match (e.g., /admin-review/123)
            const matchedKey = Object.keys(pageTitles).find((key) => path.startsWith(key));
            return matchedKey ? pageTitles[matchedKey] : "Welcome";
        };

        useEffect(() => {
            if (!notifOpen) return;

            requestAnimationFrame(() => {
                if (notifScrollRef.current) {
                    notifScrollRef.current.scrollTop = 0;
                }
            });
        }, [notifOpen]);

        useEffect(() => {
            const onResize = () => setIsSmall(window.innerWidth < 600);
            onResize();
            window.addEventListener("resize", onResize);
            return () => window.removeEventListener("resize", onResize);
        }, []);

        useEffect(() => {
            if (!uploadSummary.isUploading) return;

            const handleBeforeUnload = (event: BeforeUnloadEvent) => {
                event.preventDefault();
                event.returnValue = ACTIVE_UPLOAD_NAVIGATION_MESSAGE;
                return ACTIVE_UPLOAD_NAVIGATION_MESSAGE;
            };

            window.addEventListener("beforeunload", handleBeforeUnload);
            return () => window.removeEventListener("beforeunload", handleBeforeUnload);
        }, [uploadSummary.isUploading]);

        const logout = () => {
            if (uploadSummary.isUploading) {
                toast.error(ACTIVE_UPLOAD_NAVIGATION_MESSAGE);
                return;
            }

            dispatch(authActions.logoutRequest());
            navigate("/login", { replace: true });
        };

        const handleNotifOpenChange = (open: boolean) => {
            setNotifOpen(open);

            if (open) {
                setNotifPage(1);

                if (notifScrollRef.current) {
                    notifScrollRef.current.scrollTop = 0;
                }

                dispatch(
                    commonActions.getUserNotificationList({
                        payload: {
                            page: 1,
                            limit: 20,
                        },
                    }),
                );
            } else {
                if (notifScrollRef.current) {
                    notifScrollRef.current.scrollTop = 0;
                }
            }
        };

        const handleNotifScroll = useCallback(() => {
            const el = notifScrollRef.current;

            if (!el) return;

            const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;

            if (!nearBottom) return;

            const totalPages = notificationList?.totalPages ?? 1;
            const isLoadingMore = notificationList?.isLoadingMore ?? false;

            if (isLoadingMore) {
                return;
            }

            if (loadingNotifPageRef.current !== null) {
                return;
            }

            if (notifPage >= totalPages) {
                return;
            }

            const nextPage = notifPage + 1;

            loadingNotifPageRef.current = nextPage;

            notificationScrollState.current = {
                scrollTop: el.scrollTop,
                scrollHeight: el.scrollHeight,
            };

            setNotifPage(nextPage);

            dispatch(
                commonActions.getUserNotificationList({
                    payload: {
                        page: nextPage,
                        limit: 20,
                        append: true,
                    },
                }),
            );
        }, [notifPage, notificationList?.totalPages, notificationList?.isLoadingMore, dispatch]);

        const handleMarkAllRead = () => {
            dispatch(commonActions.markAllRead());
        };

        const handleMarkOneRead = useCallback(
            (id: string) => {
                dispatch(commonActions.markOneRead(id));
            },
            [dispatch],
        );

        const notifications: any[] = notificationList?.data || [];

        useLayoutEffect(() => {
            const savedScroll = notificationScrollState.current;
            const el = notifScrollRef.current;

            if (!savedScroll || !el) {
                return;
            }

            if (notificationList?.isLoadingMore) {
                return;
            }
            el.scrollTop = savedScroll.scrollTop;

            notificationScrollState.current = null;
            loadingNotifPageRef.current = null;
        }, [notificationList?.data, notificationList?.isLoadingMore]);

        const initials = (auth?.fullName || auth?.email || "U")
            .split(" ")
            .map((s: string) => s.charAt(0))
            .slice(0, 2)
            .join("");

        const menuItems: MenuProps["items"] = [
            { key: "/profile", label: "Profile" },
            {
                key: "/notification",
                label: (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <span>Notification Settings</span>
                    </div>
                ),
            },
            { type: "divider" as const },
            { key: "logout", label: "Logout", itemIcon: <LoginOutlined /> },
        ].filter((item) => {
            if (item.key === location.pathname) return false;
            return true;
        });

        const onMenuClick = ({ key }: { key: string }) => {
            if (key === "logout") return logout();
            if (key === "/profile") return navigate("/profile");
            if (key === "/notification") {
                return navigate("/notifications-settings");
            }
        };

        const handleNotificationView = (item: any) => {
            if (!item.isRead) {
                handleMarkOneRead(item._id);
            }

            navigate("/notifications/view", { state: { notification: item } });
            setNotifOpen(false);
        };
        return (
            <div className=" app-header" ref={headerRef}>
                <div className="app-header__left">
                    <button
                        onClick={onToggle}
                        aria-label="Toggle sidebar"
                        className="app-header__toggle"
                    >
                        {collapsed ? <PanelLeft size={20} /> : <PanelRight size={20} />}
                    </button>
                    <div className="app-header__title">
                        <h1>{getPageTitle()}</h1>
                    </div>
                </div>

                <div className="app-header__right">
                    {uploadSummary.isUploading && (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                padding: isSmall ? "6px 10px" : "6px 12px",
                                borderRadius: 999,
                                background: "#f5f3ff",
                                border: "1px solid #ddd6fe",
                                minWidth: isSmall ? undefined : 200,
                            }}
                            aria-live="polite"
                        >
                            <Spinner />
                            {!isSmall && (
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        minWidth: 0,
                                    }}
                                >
                                    <Text
                                        strong
                                        style={{ fontSize: 12, lineHeight: 1.2, color: "#4338ca" }}
                                    >
                                        {uploadLabel}
                                    </Text>
                                    <Progress
                                        percent={uploadSummary.averageProgress}
                                        size="small"
                                        status="active"
                                        strokeColor="#4f46e5"
                                        style={{ width: 150, margin: 0 }}
                                    />
                                </div>
                            )}
                            {isSmall && (
                                <Text strong style={{ fontSize: 12, color: "#4338ca" }}>
                                    {uploadSummary.averageProgress}%
                                </Text>
                            )}
                        </div>
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <Badge
                            count={notificationCount}
                            overflowCount={99}
                            size="small"
                            offset={[-2, 2]}
                            styles={{
                                indicator: { backgroundColor: "#4f46e5", boxShadow: "none" },
                            }}
                        >
                            <Popover
                                open={notifOpen}
                                onOpenChange={handleNotifOpenChange}
                                trigger="click"
                                placement="bottomRight"
                                arrow={true}
                                content={
                                    <div style={{ width: 360 }}>
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                marginBottom: 8,
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 15,
                                                    color: "#1e1b4b",
                                                }}
                                            >
                                                Notifications
                                            </span>
                                            {notificationCount > 0 && (
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    icon={<CheckOutlined />}
                                                    onClick={handleMarkAllRead}
                                                    style={{
                                                        color: "#4f46e5",
                                                        padding: 0,
                                                        fontSize: 12,
                                                    }}
                                                >
                                                    Mark all as read
                                                </Button>
                                            )}
                                        </div>
                                        <Divider style={{ margin: "8px 0" }} />
                                        {notificationList?.isLoading ? (
                                            <div style={{ textAlign: "center", padding: "24px 0" }}>
                                                <Spin />
                                            </div>
                                        ) : notifications.length === 0 ? (
                                            <Empty
                                                description="No notifications"
                                                imageStyle={{ height: 48 }}
                                                style={{ padding: "24px 0" }}
                                            />
                                        ) : (
                                            <div
                                                ref={notifScrollRef}
                                                onScroll={handleNotifScroll}
                                                style={{
                                                    maxHeight: 380,
                                                    overflowY: "auto",
                                                    overflowAnchor: "none",
                                                    position: "relative",
                                                    paddingBottom: notificationList?.isLoadingMore
                                                        ? 44
                                                        : 0,
                                                }}
                                            >
                                                <List
                                                    dataSource={notifications}
                                                    renderItem={(item: any) => (
                                                        <List.Item
                                                            style={{
                                                                padding: "10px 4px",
                                                                background: item.isRead
                                                                    ? "transparent"
                                                                    : "#f5f3ff",
                                                                borderRadius: 8,
                                                                marginBottom: 4,
                                                                alignItems: "flex-start",
                                                                cursor: "pointer",
                                                            }}
                                                            onClick={() =>
                                                                handleNotificationView(item)
                                                            }
                                                        >
                                                            <div
                                                                style={{
                                                                    display: "flex",
                                                                    gap: 10,
                                                                    width: "100%",
                                                                }}
                                                            >
                                                                {!item.isRead && (
                                                                    <div
                                                                        style={{
                                                                            width: 8,
                                                                            height: 8,
                                                                            borderRadius: "50%",
                                                                            background: "#4f46e5",
                                                                            marginTop: 6,
                                                                            flexShrink: 0,
                                                                        }}
                                                                    />
                                                                )}
                                                                <div
                                                                    style={{ flex: 1, minWidth: 0 }}
                                                                >
                                                                    {item.title && (
                                                                        <div
                                                                            style={{
                                                                                fontWeight: 600,
                                                                                fontSize: 13,
                                                                                color: "#1e1b4b",
                                                                                marginBottom: 2,
                                                                            }}
                                                                        >
                                                                            {item.title}
                                                                        </div>
                                                                    )}
                                                                    <div
                                                                        style={{
                                                                            fontSize: 12,
                                                                            color: "#4b5563",
                                                                            lineHeight: 1.5,
                                                                        }}
                                                                    >
                                                                        {item.message}
                                                                    </div>
                                                                    <div
                                                                        style={{
                                                                            fontSize: 11,
                                                                            color: "#9ca3af",
                                                                            marginTop: 4,
                                                                        }}
                                                                    >
                                                                        {new Date(
                                                                            item.createdAt,
                                                                        ).toLocaleString("en-IN", {
                                                                            dateStyle: "medium",
                                                                            timeStyle: "short",
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </List.Item>
                                                    )}
                                                />
                                                {notificationList?.isLoadingMore && (
                                                    <div
                                                        style={{
                                                            position: "sticky",
                                                            bottom: 0,
                                                            zIndex: 10,
                                                            height: 44,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            background:
                                                                "linear-gradient(to top, rgba(255,255,255,0.98), rgba(255,255,255,0.85), transparent)",
                                                            pointerEvents: "none",
                                                        }}
                                                    >
                                                        <Spin size="small" />
                                                    </div>
                                                )}
                                                {!notificationList?.isLoadingMore &&
                                                    notifPage >=
                                                        (notificationList?.totalPages ?? 1) &&
                                                    notifications.length > 0 && (
                                                        <div
                                                            style={{
                                                                textAlign: "center",
                                                                fontSize: 11,
                                                                color: "#9ca3af",
                                                                padding: "6px 0",
                                                            }}
                                                        >
                                                            All {notificationList?.total}{" "}
                                                            notifications loaded
                                                        </div>
                                                    )}
                                            </div>
                                        )}
                                    </div>
                                }
                            >
                                <button
                                    aria-label={`Notifications${notificationCount > 0 ? `, ${notificationCount} unread` : ""}`}
                                    className="app-header__notification"
                                    onMouseEnter={(e) =>
                                        (e.currentTarget.style.background = "#f1f5f9")
                                    }
                                    onMouseLeave={(e) =>
                                        (e.currentTarget.style.background = "transparent")
                                    }
                                >
                                    <Bell size={20} />
                                </button>
                            </Popover>
                        </Badge>
                        <div className="app-header__divider" />
                        <Dropdown
                            menu={{
                                items: menuItems,
                                onClick: onMenuClick,
                                className: "custom-dropdown-menu",
                            }}
                            placement="bottomRight"
                            trigger={["click"]}
                        >
                            <a onClick={(e) => e.preventDefault()} className="app-header__profile">
                                <Avatar
                                    className="app-header__avatar"
                                    icon={!initials ? <UserOutlined /> : undefined}
                                >
                                    {initials ? initials.toUpperCase() : null}
                                </Avatar>

                                {!isSmall && (
                                    <div className="app-header__user">
                                        <span className="app-header__user-name">
                                            {auth?.firstName && auth?.lastName
                                                ? `${titleCase(auth.firstName)} ${titleCase(auth.lastName)}`
                                                : auth?.fullName || auth?.email || "User"}
                                        </span>
                                        <span className="app-header__user-role">
                                            {auth?.role || ""}
                                        </span>
                                    </div>
                                )}
                                <DownOutlined className="app-header__arrow" />
                            </a>
                        </Dropdown>
                    </div>
                </div>
            </div>
        );
    },
);

export default HeaderBar;
